import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Loader2, PartyPopper, Heart, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { ResponsePicker } from "./ResponsePicker";
import { GuestStepper } from "./GuestStepper";
import { CalendarActions } from "./CalendarActions";
import { BrandLogo } from "./BrandLogo";
import { api, formatApiError } from "../lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const ICONS = { yes: PartyPopper, maybe: Sparkles, no: Heart };

const burst = () =>
  confetti({ particleCount: 160, spread: 90, origin: { y: 0.6 }, colors: ["#ec4899", "#f59e0b", "#10b981", "#8b5cf6", "#06b6d4", "#1d4ed8", "#ef4444"] });

const DialogBrand = () => (
  <div className="mt-6 flex justify-center border-t border-slate-100 pt-5" data-testid="rsvp-dialog-brand">
    <BrandLogo size="sm" to={null} />
  </div>
);

const Success = ({ result, event, onEdit, onClose }) => {
  const Icon = ICONS[result.rsvp.response];
  return (
    <div data-testid="rsvp-success" className="rise py-2 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-blue-700">
        <Icon className="h-8 w-8" />
      </div>
      <p data-testid="rsvp-success-message" className="font-display mt-5 text-2xl font-bold tracking-tight text-slate-900">
        {result.message}
      </p>
      {result.added_additional && (
        <p data-testid="rsvp-additional-total-note" className="mt-2 text-sm text-slate-500">
          Total additional guests: {result.rsvp.extra_guests}.
        </p>
      )}
      {result.updated && !result.added_additional && (
        <p data-testid="rsvp-updated-note" className="mt-2 text-sm text-slate-500">
          We updated your earlier response.
        </p>
      )}
      {result.rsvp.response !== "no" && event.date && <CalendarActions event={event} />}
      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
        <Button variant="outline" className="rounded-lg" data-testid="rsvp-edit-again-button" onClick={onEdit}>
          Change my response
        </Button>
        <Button className="rounded-lg" data-testid="rsvp-close-button" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
};

const Field = ({ label, error, children, testid }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</Label>
    {children}
    {error && <p data-testid={testid} className="text-xs font-semibold text-red-600">{error}</p>}
  </div>
);

const AlreadyRegistered = ({ prompt, adding, onAdd, onDecline, onChangeResponse }) => {
  const remaining = prompt.remaining_additional_guests || 0;
  const [more, setMore] = useState(remaining > 0 ? 1 : 0);
  const limit = prompt.max_additional_guests;
  return (
    <div data-testid="rsvp-already-registered" className="py-2">
      <p data-testid="rsvp-already-registered-message" className="font-display text-2xl font-bold tracking-tight text-slate-900">
        {prompt.message}
      </p>
      {prompt.can_add_guests ? (
        <>
          <p className="mt-3 text-sm text-slate-500">
            You can add {remaining} more {remaining === 1 ? "guest" : "guests"} (up to {limit} total).
          </p>
          <div className="mt-5">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">How many more guests?</p>
            <GuestStepper value={more} min={1} max={remaining} onChange={setMore} testidPrefix="rsvp-add-guests" />
          </div>
          <div className="mt-6 flex flex-col gap-2">
            <Button type="button" disabled={adding} data-testid="rsvp-add-guests-confirm" className="h-12 rounded-lg text-base font-semibold" onClick={() => onAdd(more)}>
              {adding ? <Loader2 className="h-5 w-5 animate-spin" /> : "Yes, add guests"}
            </Button>
            <Button type="button" variant="outline" data-testid="rsvp-add-guests-decline" className="rounded-lg" onClick={onDecline}>
              No thanks
            </Button>
          </div>
        </>
      ) : (
        <>
          <p data-testid="rsvp-add-guests-blocked" className="mt-3 text-sm text-slate-500">
            {prompt.rsvp?.response === "no"
              ? "Additional guests can be added when your RSVP is Yes or Maybe."
              : limit === 0
                ? "This invitation doesn't allow additional guests."
                : `This invitation allows up to ${limit} additional ${limit === 1 ? "guest" : "guests"}, and you're already at that limit.`}
          </p>
          <Button type="button" data-testid="rsvp-add-guests-decline" className="mt-6 h-12 w-full rounded-lg" onClick={onDecline}>
            Keep my RSVP
          </Button>
        </>
      )}
      <button type="button" data-testid="rsvp-change-response-btn" onClick={onChangeResponse} className="mt-4 w-full text-sm font-semibold text-blue-700 hover:text-blue-800">
        Change my response instead
      </button>
    </div>
  );
};

const EMPTY_FORM = { name: "", email: "", phone: "", response: "yes", extra_guests: 0, note: "", public_comment: "" };
const TEXT_MAX = 500;
const PHONE_RE = /^[0-9\s+\-()]+$/;
const fieldLabel = (text, mode) => `${text}${mode === "required" ? " *" : " (optional)"}`;

export const RsvpDialog = ({ open, onOpenChange, event, onSubmitted }) => {
  const storageKey = `pi_rsvp_${event.slug}`;
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...JSON.parse(localStorage.getItem(storageKey) || "null") }));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const editing = useRef(false);
  const max = event.max_additional_guests;
  const modes = { email: event.email_field || "required", phone: event.phone_field || "off", note: event.note_field || "optional", public_comment: event.comment_field || "off" };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setResult(null);
      setPrompt(null);
      editing.current = false;
    }
  }, [open]);

  const validate = () => {
    const errs = {};
    const email = form.email.trim();
    const phone = form.phone.trim();
    const digits = phone.replace(/\D/g, "").length;
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (modes.email === "required" && !email) errs.email = "Email is required";
    else if (modes.email !== "off" && email && !EMAIL_RE.test(email)) errs.email = "Please enter a valid email";
    if (modes.phone === "required" && !phone) errs.phone = "Phone number is required";
    else if (modes.phone !== "off" && phone && (!PHONE_RE.test(phone) || digits < 7 || digits > 15)) errs.phone = "Enter a valid phone number (7-15 digits)";
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const buildPayload = (extra = {}) => {
    const payload = {
      name: form.name.trim(),
      response: form.response,
      extra_guests: form.response === "no" ? 0 : Math.min(form.extra_guests, max),
      ...extra,
    };
    ["email", "phone"].forEach((k) => {
      if (modes[k] !== "off") payload[k] = form[k].trim();
    });
    // Send note/comment only if typed, or if clearing a value this device sent before.
    const saved = JSON.parse(localStorage.getItem(storageKey) || "{}");
    ["note", "public_comment"].forEach((k) => {
      const value = form[k].trim();
      if (modes[k] !== "off" && (value || saved[k])) payload[k] = value;
    });
    if (editing.current) payload.update_existing = true;
    return payload;
  };

  const finish = (data, payload) => {
    const next = { ...form, ...payload, extra_guests: data.rsvp.extra_guests, response: data.rsvp.response };
    setForm((f) => ({ ...f, extra_guests: data.rsvp.extra_guests, response: data.rsvp.response }));
    localStorage.setItem(storageKey, JSON.stringify(next));
    setPrompt(null);
    setResult(data);
    onSubmitted?.();
    if (data.rsvp.response !== "no") burst();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const payload = buildPayload();
    try {
      const { data } = await api.post(`/public/events/${event.slug}/rsvp`, payload);
      if (data.already_registered) {
        setPrompt(data);
        return;
      }
      finish(data, payload);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const addGuests = async (more) => {
    setSubmitting(true);
    const payload = buildPayload({ add_additional_guests: more });
    try {
      const { data } = await api.post(`/public/events/${event.slug}/rsvp`, payload);
      finish(data, payload);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const changeResponse = () => {
    editing.current = true;
    if (prompt?.rsvp) {
      setForm((f) => ({
        ...f,
        response: prompt.rsvp.response,
        extra_guests: prompt.current_extra_guests || 0,
      }));
    }
    setPrompt(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rsvp-dialog" className="max-h-[92vh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto rounded-3xl border-0 p-6 sm:p-8">
        {result ? (
          <Success result={result} event={event} onEdit={() => { editing.current = true; setResult(null); }} onClose={() => onOpenChange(false)} />
        ) : prompt ? (
          <AlreadyRegistered
            prompt={prompt}
            adding={submitting}
            onAdd={addGuests}
            onDecline={() => onOpenChange(false)}
            onChangeResponse={changeResponse}
          />
        ) : (
          <>
            <DialogHeader className="text-left">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700">RSVP</p>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">{event.occasion}</DialogTitle>
              <DialogDescription>Let the host know if you can make it.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 space-y-5" noValidate data-testid="rsvp-form">
              <Field label="Your name *" error={errors.name} testid="rsvp-name-error">
                <Input data-testid="rsvp-name-input" className="h-12 rounded-xl" value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="Jamie Rivera" />
              </Field>
              {modes.email !== "off" && (
                <Field label={fieldLabel("Email", modes.email)} error={errors.email} testid="rsvp-email-error">
                  <Input data-testid="rsvp-email-input" type="email" inputMode="email" autoComplete="email" className="h-12 rounded-xl" value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="you@example.com" />
                </Field>
              )}
              {modes.phone !== "off" && (
                <Field label={fieldLabel("Phone", modes.phone)} error={errors.phone} testid="rsvp-phone-error">
                  <Input data-testid="rsvp-phone-input" type="tel" inputMode="tel" autoComplete="tel" className="h-12 rounded-xl" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="+1 (555) 123-4567" />
                </Field>
              )}
              <Field label="Will you join?">
                <ResponsePicker value={form.response} onChange={set("response")} />
              </Field>
              {form.response !== "no" && max > 0 && (
                <Field label="Additional guests">
                  <GuestStepper value={form.extra_guests} max={max} onChange={set("extra_guests")} />
                </Field>
              )}
              {modes.public_comment !== "off" && (
                <Field label={fieldLabel("Public comment", "optional")}>
                  <Textarea data-testid="rsvp-comment-input" className="rounded-xl" rows={2} maxLength={TEXT_MAX} value={form.public_comment} onChange={(e) => set("public_comment")(e.target.value)} placeholder="Can't wait to celebrate!" />
                  <p className="text-xs text-slate-400">Other guests can see this.</p>
                </Field>
              )}
              {modes.note !== "off" && (
                <Field label={fieldLabel("Private note to host", "optional")}>
                  <Textarea data-testid="rsvp-note-input" className="rounded-xl" rows={2} maxLength={TEXT_MAX} value={form.note} onChange={(e) => set("note")(e.target.value)} placeholder="Allergies, running late..." />
                  <p className="text-xs text-slate-400">Only the host sees this.</p>
                </Field>
              )}
              <Button type="submit" disabled={submitting} data-testid="rsvp-submit-button" className="h-12 w-full rounded-lg text-base font-semibold">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send RSVP"}
              </Button>
            </form>
          </>
        )}
        <DialogBrand />
      </DialogContent>
    </Dialog>
  );
};
